import { domainError, type DomainError } from '../shared/domain-error';
import { err, ok, type Result } from '../shared/result';
import type { FloorPlanId, FloorPlanSheetId, ProjectId, UserId } from '../shared/ids';
import {
  attachmentChecksum,
  isImage,
  sniffMimeType,
  type SupportedMimeType,
} from '../attachments/attachment';

/**
 * A prancha do pavimento — o arquivo sobre o qual o desenho foi traçado (Fase 24, D23).
 *
 * Até aqui ela era uma imagem renderizada guardada no `localStorage` de quem a carregou:
 * outra máquina abria a planta sem prancha, e o traçado aparecia solto no vazio. Limpar o
 * navegador levava junto a escala, e com ela a única referência que dizia **onde** cada
 * parede tinha sido desenhada.
 *
 * Três decisões moram aqui:
 *
 * - **uma viva por desenho de pavimento** (RN-246). A prancha é do pavimento, e não da
 *   tipologia: o apto 01, o 02 e a circulação são traçados na mesma folha, e é isso que
 *   faz a divisa entre eles aparecer por geometria. Trocar é anexar por cima — e só vale
 *   contra a prancha que quem trocou **viu**;
 * - **a calibragem independe da renderização** (RN-247). Ela é guardada como metros por
 *   pixel **junto da largura em que foi medida**, e qualquer renderização nova reencontra
 *   a mesma escala. O traçado já é guardado em metros; é esta conta que o mantém em cima
 *   das paredes da folha quando o PDF é rasterizado de novo, em outra largura;
 * - **o original é o que se guarda, até onde o caminho deixa** (RN-248). Acima do limite,
 *   guarda-se a página renderizada, marcada como tal — e nunca uma falha muda.
 *
 * O binário mora onde o do anexo já mora (`FileStorage`, ADR-109), endereçado por
 * `blobKey`. O registro fica em tabela própria, e não em `attachments` (ADR-145): foto
 * de conferência é comprimida a 1600 px, limitada a 10 MB e arquivada quando envelhece —
 * as três coisas que uma prancha não pode sofrer.
 */

/**
 * De onde veio o arquivo guardado (RN-248).
 *
 * - `ORIGINAL` — o PDF ou a imagem que a pessoa escolheu, byte a byte;
 * - `RENDERIZADA` — a página renderizada pelo navegador, porque o original passou do
 *   limite que o caminho até o servidor aceita. Serve ao traço do mesmo jeito; perde o
 *   que o vetor do PDF daria no zoom máximo;
 * - `NAVEGADOR` — a imagem que estava no `localStorage` de antes desta fase, subida uma
 *   vez para ninguém perder o trabalho. O PDF original já não existia mais em lugar
 *   nenhum, e a tela diz isso em vez de fingir que é ele.
 */
export type FloorPlanSheetSource = 'ORIGINAL' | 'RENDERIZADA' | 'NAVEGADOR';

export const FLOOR_PLAN_SHEET_SOURCES: readonly FloorPlanSheetSource[] = [
  'ORIGINAL',
  'RENDERIZADA',
  'NAVEGADOR',
];

/**
 * A escala da prancha, e a largura em que ela foi medida (RN-247).
 *
 * Metros por pixel sozinho não basta: o pixel é da **renderização**, e a mesma folha
 * rasterizada a 3000 px e a 2400 px tem pixels de tamanhos diferentes. Guardar a largura
 * junto é o que deixa a conta ser refeita para qualquer renderização — e, na renderização
 * de sempre, devolve exatamente o número gravado, sem arredondamento nenhum no meio.
 */
export interface SheetCalibration {
  readonly metresPerPixel: number;
  /** Largura, em pixels, da renderização sobre a qual os dois pontos foram clicados. */
  readonly renderWidth: number;
}

export interface FloorPlanSheet {
  readonly id: FloorPlanSheetId;
  readonly projectId: ProjectId;
  readonly floorPlanId: FloorPlanId;
  /** Endereço do binário no `FileStorage` — nunca os bytes. */
  readonly blobKey: string;
  readonly originalName: string;
  readonly mimeType: SupportedMimeType;
  /** Tamanho do arquivo guardado. */
  readonly sizeBytes: number;
  readonly checksum: string;
  readonly source: FloorPlanSheetSource;
  /** Ausente = ainda não calibrada, e nada pode ser traçado sobre ela. */
  readonly calibration?: SheetCalibration;
  readonly attachedByUserId: UserId;
  readonly attachedAt: string;
  readonly updatedAt: string;
  /** Removida ou trocada. O registro fica: é ele que diz quem anexou o que, e quando. */
  readonly deletedAt?: string;
}

// ── Limite (RN-248) ─────────────────────────────────────────────────────────────

/**
 * 18 MB — o maior arquivo que atravessa o caminho até o servidor.
 *
 * Não é gosto, é conta. O arquivo sobe em base64 dentro do JSON (`withBytes`), e base64
 * engorda um terço: 18 MB viram 24 MB de corpo. O nginx da Lightsail recusa acima de
 * `client_max_body_size 25m` (`deploy/lightsail/obraplan.nginx.conf`), e o que sobra é a
 * folga dos outros campos. Subir este número exige subir aquele antes.
 *
 * Planta em PDF costuma ter de 1 a 20 MB. A que passar não é recusada: o navegador guarda
 * a página renderizada no lugar, com a mesma escala, e diz que o fez.
 */
export const MAX_PLAN_SHEET_BYTES = 18 * 1024 * 1024;

// ── Erros ───────────────────────────────────────────────────────────────────────

export const FLOOR_PLAN_SHEET_ERROR = {
  FILE_EMPTY: 'TAKEOFF.SHEET_FILE_EMPTY',
  FILE_TOO_LARGE: 'TAKEOFF.SHEET_FILE_TOO_LARGE',
  UNSUPPORTED_TYPE: 'TAKEOFF.SHEET_UNSUPPORTED_TYPE',
  RENDERED_MUST_BE_IMAGE: 'TAKEOFF.SHEET_RENDERED_MUST_BE_IMAGE',
  INVALID_CALIBRATION: 'TAKEOFF.SHEET_INVALID_CALIBRATION',
  NOT_FOUND: 'TAKEOFF.SHEET_NOT_FOUND',
  REPLACED_MEANWHILE: 'TAKEOFF.SHEET_REPLACED_MEANWHILE',
} as const;

export const FLOOR_PLAN_SHEET_ERROR_MESSAGE: Readonly<Record<string, string>> = {
  [FLOOR_PLAN_SHEET_ERROR.FILE_EMPTY]: 'O arquivo da prancha está vazio.',
  [FLOOR_PLAN_SHEET_ERROR.FILE_TOO_LARGE]:
    'O arquivo passa de 18 MB, o limite para guardar o original no servidor.',
  [FLOOR_PLAN_SHEET_ERROR.UNSUPPORTED_TYPE]: 'A prancha precisa ser PDF, JPEG, PNG ou WebP.',
  [FLOOR_PLAN_SHEET_ERROR.RENDERED_MUST_BE_IMAGE]:
    'A página renderizada pelo navegador precisa chegar como imagem.',
  [FLOOR_PLAN_SHEET_ERROR.INVALID_CALIBRATION]:
    'A escala precisa de metros por pixel e da largura da prancha, os dois maiores que zero.',
  [FLOOR_PLAN_SHEET_ERROR.NOT_FOUND]: 'Prancha não encontrada nesta obra.',
  [FLOOR_PLAN_SHEET_ERROR.REPLACED_MEANWHILE]:
    'Esta planta ganhou outra prancha enquanto a tela estava aberta. Recarregue para ver quem a anexou antes de trocar.',
};

function erro(code: string, details?: Record<string, unknown>): DomainError {
  return domainError(code, FLOOR_PLAN_SHEET_ERROR_MESSAGE[code]!, details);
}

// ── Validação ───────────────────────────────────────────────────────────────────

export interface SheetCandidate {
  readonly fileName: string;
  /** O tipo que o navegador declarou. Pista, nunca autoridade — como no anexo (RN-105). */
  readonly declaredMimeType: string;
  readonly bytes: Uint8Array;
  readonly source: FloorPlanSheetSource;
}

/**
 * O arquivo pode virar prancha?
 *
 * A mesma ordem do anexo, pelo mesmo motivo: tamanho antes de conteúdo, porque não se
 * inspeciona byte a byte um arquivo que vai ser recusado pelo tamanho; e conteúdo antes de
 * qualquer escrita. O tipo gravado é o que os primeiros bytes dizem (`sniffMimeType`).
 *
 * A renderização e a imagem que veio do navegador só podem ser imagem: quem as produz é o
 * `canvas`, e um PDF chegando com essa marca seria a marca errada — o que a tela diria
 * sobre ele ("página renderizada") seria mentira.
 */
export function validateSheetFile(
  candidate: SheetCandidate,
): Result<{ readonly mimeType: SupportedMimeType; readonly sizeBytes: number }, DomainError> {
  const sizeBytes = candidate.bytes.byteLength;
  if (sizeBytes === 0) {
    return err(erro(FLOOR_PLAN_SHEET_ERROR.FILE_EMPTY, { fileName: candidate.fileName }));
  }
  if (sizeBytes > MAX_PLAN_SHEET_BYTES) {
    return err(
      erro(FLOOR_PLAN_SHEET_ERROR.FILE_TOO_LARGE, {
        fileName: candidate.fileName,
        sizeBytes,
        limit: MAX_PLAN_SHEET_BYTES,
      }),
    );
  }

  const mimeType = sniffMimeType(candidate.bytes);
  if (mimeType === undefined) {
    return err(
      erro(FLOOR_PLAN_SHEET_ERROR.UNSUPPORTED_TYPE, {
        fileName: candidate.fileName,
        declared: candidate.declaredMimeType,
      }),
    );
  }
  if (candidate.source !== 'ORIGINAL' && !isImage(mimeType)) {
    return err(erro(FLOOR_PLAN_SHEET_ERROR.RENDERED_MUST_BE_IMAGE, { source: candidate.source }));
  }

  return ok({ mimeType, sizeBytes });
}

/** Escala que se possa usar: os dois números finitos e maiores que zero. */
export function parseCalibration(input: {
  readonly metresPerPixel: number;
  readonly renderWidth: number;
}): Result<SheetCalibration, DomainError> {
  const { metresPerPixel, renderWidth } = input;
  if (
    !Number.isFinite(metresPerPixel) ||
    metresPerPixel <= 0 ||
    !Number.isInteger(renderWidth) ||
    renderWidth <= 0
  ) {
    return err(erro(FLOOR_PLAN_SHEET_ERROR.INVALID_CALIBRATION, { metresPerPixel, renderWidth }));
  }
  return ok({ metresPerPixel, renderWidth });
}

/**
 * Metros por pixel numa renderização desta largura (RN-247).
 *
 * A mesma folha, mais larga, tem pixels menores — e na mesma proporção. Na largura em que
 * a calibragem foi feita, devolve o número gravado **sem fazer conta**: a divisão por ela
 * mesma daria o mesmo número quase sempre, e o "quase" é o que deslocaria o traçado de um
 * ulp a cada recarga.
 */
export function metresPerPixelAt(calibration: SheetCalibration, renderWidth: number): number {
  if (renderWidth === calibration.renderWidth) return calibration.metresPerPixel;
  return (calibration.metresPerPixel * calibration.renderWidth) / renderWidth;
}

// ── Ciclo de vida ───────────────────────────────────────────────────────────────

/**
 * Endereço do binário no armazenamento.
 *
 * Prefixo próprio, e não o do anexo: as duas famílias dividem `attachment_blobs`, e quem
 * olhar a tabela precisa saber de quem é cada linha sem cruzar com mais nada.
 */
export function floorPlanSheetBlobKey(id: FloorPlanSheetId): string {
  return `floor-plan-sheet/${id}`;
}

export function createFloorPlanSheet(input: {
  readonly id: FloorPlanSheetId;
  readonly projectId: ProjectId;
  readonly floorPlanId: FloorPlanId;
  readonly originalName: string;
  readonly mimeType: SupportedMimeType;
  readonly bytes: Uint8Array;
  readonly source: FloorPlanSheetSource;
  readonly calibration?: SheetCalibration;
  readonly attachedByUserId: UserId;
  readonly now: string;
}): FloorPlanSheet {
  return {
    id: input.id,
    projectId: input.projectId,
    floorPlanId: input.floorPlanId,
    blobKey: floorPlanSheetBlobKey(input.id),
    originalName: input.originalName.trim(),
    mimeType: input.mimeType,
    sizeBytes: input.bytes.byteLength,
    checksum: attachmentChecksum(input.bytes),
    source: input.source,
    attachedByUserId: input.attachedByUserId,
    attachedAt: input.now,
    updatedAt: input.now,
    ...(input.calibration === undefined ? {} : { calibration: input.calibration }),
  };
}

export function calibrateSheet(
  sheet: FloorPlanSheet,
  calibration: SheetCalibration,
  now: string,
): FloorPlanSheet {
  return { ...sheet, calibration, updatedAt: now };
}

/** Remover e trocar são o mesmo gesto para a prancha que sai: ela deixa de valer. */
export function removeSheet(sheet: FloorPlanSheet, now: string): FloorPlanSheet {
  return { ...sheet, deletedAt: now, updatedAt: now };
}

export function isLiveSheet(sheet: FloorPlanSheet): boolean {
  return sheet.deletedAt === undefined;
}

/**
 * Anexar agora pode passar por cima da prancha que está lá? (RN-246)
 *
 * A troca é a prancha **que quem trocou viu** — e é por isso que a tela manda o id dela.
 * Sem esta conferência, duas pessoas com a planta aberta trocariam uma a prancha da
 * outra sem aviso, e o aviso "anexada por X" teria sido lido sobre a prancha errada.
 *
 * Nada no lugar, qualquer pedido passa: se a que ia ser trocada já saiu, anexar é só
 * anexar.
 */
export function mayReplace(
  live: FloorPlanSheet | undefined,
  replacesSheetId: string | undefined,
): boolean {
  return live === undefined || live.id === replacesSheetId;
}
